import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Field } from "@/components/admin/ui";
import { BUSINESS_ITEM_TYPE_LABELS, formatCents } from "./salesMeta";
import { ChevronDown, ChevronRight, Package, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const catalogOptionsFor = (itemType, meta) => {
  if (itemType === "service") return meta.services || [];
  if (itemType === "product") return meta.products || [];
  return meta.bundles || [];
};

const idFieldFor = (itemType) => ({ service: "service_id", product: "product_id", bundle: "bundle_id" }[itemType]);

const BusinessItemRow = ({ item, busy, onQuantityChange, onRemove }) => {
  const [expanded, setExpanded] = useState(false);
  const catalog = item.catalog;
  const imageUrl = catalog?.image_url ? `${BACKEND_URL}${catalog.image_url}` : null;

  return (
    <div className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`lead-business-item-${item.business_item_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 dark:bg-white/5">
          {imageUrl ? <img src={imageUrl} alt={catalog?.name} className="h-full w-full object-cover" /> : <Package size={18} className="text-slate-300 dark:text-slate-600" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900 dark:text-white">
            {item.item_type === "bundle" && (
              <button type="button" onClick={() => setExpanded((v) => !v)} data-testid={`lead-business-item-expand-${item.business_item_id}`}>
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            )}
            {catalog?.name || item.item_name_snapshot}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-white/10 dark:text-slate-300">{BUSINESS_ITEM_TYPE_LABELS[item.item_type]}</span>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {catalog?.sku && <>SKU {catalog.sku} · </>}
            {formatCents(catalog?.price_cents) || "No price set"}
            {catalog?.duration_minutes ? ` · ${catalog.duration_minutes} min` : ""}
          </p>
        </div>
        <input
          type="number" min={1} max={999} value={item.quantity}
          onChange={(e) => onQuantityChange(item.business_item_id, Math.max(1, Number(e.target.value) || 1))}
          disabled={busy}
          data-testid={`lead-business-item-qty-${item.business_item_id}`}
          className="w-16 rounded-lg border border-slate-200 px-2 py-1.5 text-center text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white"
        />
        <button type="button" onClick={() => onRemove(item.business_item_id)} disabled={busy} data-testid={`lead-business-item-remove-${item.business_item_id}`} className="text-slate-400 hover:text-red-600 dark:hover:text-red-400">
          <X size={16} />
        </button>
      </div>
      {expanded && item.bundle_contents?.length > 0 && (
        <div className="ml-6 mt-2 space-y-1 border-l border-slate-200 pl-3 dark:border-white/10" data-testid={`lead-business-item-contents-${item.business_item_id}`}>
          {item.bundle_contents.map((c) => (
            <p key={`${c.item_type}-${c.item_id}`} className="text-xs text-slate-500 dark:text-slate-400">
              {c.name} — {formatCents(c.price_cents) || "—"}{c.duration_minutes ? ` · ${c.duration_minutes} min` : ""}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export const LeadBusinessTab = ({ lead, meta, businessItems, onRefresh, onError, API }) => {
  const [busy, setBusy] = useState(false);
  const [itemType, setItemType] = useState("service");
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState(1);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onRefresh(); } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addItem = () => {
    if (!itemId) return;
    run(async () => {
      await axios.post(`${API}/org/sales/leads/${lead.lead_id}/business-items`, { item_type: itemType, item_id: itemId, quantity }, { withCredentials: true });
      setItemId(""); setQuantity(1);
    });
  };

  const updateQuantity = (businessItemId, newQty) => run(() => axios.patch(`${API}/org/sales/leads/${lead.lead_id}/business-items/${businessItemId}`, { quantity: newQty }, { withCredentials: true }));
  const removeItem = (businessItemId) => run(() => axios.delete(`${API}/org/sales/leads/${lead.lead_id}/business-items/${businessItemId}`, { withCredentials: true }));

  const options = catalogOptionsFor(itemType, meta);
  const idField = idFieldFor(itemType);

  return (
    <div className="space-y-5" data-testid="lead-tab-panel-business">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Original Request" value={lead.description || "No description was submitted."} testId="lead-business-description" />
        <Field label="Requested Service" value={lead.requested_service_name || "—"} testId="lead-business-requested-service" />
      </div>

      <div className="border-t border-slate-200 pt-4 dark:border-white/10">
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Attach Catalog Items</p>
        <div className="flex flex-wrap items-end gap-2">
          <select value={itemType} onChange={(e) => { setItemType(e.target.value); setItemId(""); }} data-testid="lead-business-item-type-select" className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            {Object.entries(BUSINESS_ITEM_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={itemId} onChange={(e) => setItemId(e.target.value)} data-testid="lead-business-item-select" className="min-w-[220px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">Choose a {BUSINESS_ITEM_TYPE_LABELS[itemType].toLowerCase()}…</option>
            {options.map((o) => (
              <option key={o[idField]} value={o[idField]}>
                {o.name}{o.sku ? ` (${o.sku})` : ""}{o.price_cents != null ? ` — ${formatCents(o.price_cents)}` : ""}
              </option>
            ))}
          </select>
          <input type="number" min={1} max={999} value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))} data-testid="lead-business-item-quantity-input" className="w-16 rounded-lg border border-slate-200 px-2 py-2 text-center text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
          <button type="button" onClick={addItem} disabled={busy || !itemId} data-testid="lead-business-item-add-btn" className="rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50">
            Add
          </button>
        </div>
        {options.length === 0 && <p className="mt-2 text-xs text-slate-400">No active {BUSINESS_ITEM_TYPE_LABELS[itemType].toLowerCase()}s exist in your catalog yet.</p>}
      </div>

      <div className="space-y-2" data-testid="lead-business-items-list">
        {businessItems.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">No catalog items attached yet.</p>
        ) : (
          businessItems.map((item) => (
            <BusinessItemRow key={item.business_item_id} item={item} busy={busy} onQuantityChange={updateQuantity} onRemove={removeItem} />
          ))
        )}
      </div>
    </div>
  );
};
